/**
 * The administration area: its guard, its sub-navigation, and the small pieces
 * of chrome every page inside it is built from.
 *
 * Three things live here rather than in a page because they are shared by all
 * of them. The **unsaved-work guard** has to sit above the navigation to be
 * able to interrupt it. The **field readers** exist because several admin
 * endpoints answer with more than the typed shapes in `@/api/types` enumerate —
 * the contract says a client must ignore unknown fields, and a diagnostics page
 * is exactly where the extra ones are worth showing when the server sends them.
 * And the **page chrome** is what keeps eight pages looking like one screen.
 */

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom'

import { useRequireAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useAdminUpdateCheck } from '@/api/queries'
import { Badge, Button, FullPageMessage, FullPageSpinner, Modal, useToast } from '@/components/ui'

import { AuditPage } from './AuditPage'
import { FederationPage } from './FederationPage'
import { InvitationsPage } from './InvitationsPage'
import { SettingsPage } from './SettingsPage'
import { NotificationsPage } from './NotificationsPage'
import { StatusPage } from './StatusPage'
import { StoragePage } from './StoragePage'
import { UsersPage } from './UsersPage'
import { WebhooksPage } from './WebhooksPage'
import { WidgetsPage } from './WidgetsPage'
import { ApiKeysPage } from './ApiKeysPage'

import layout from './AdminLayout.module.css'
import styles from './admin.module.css'

/**
 * Joins class names, dropping the absent ones. CSS module lookups are typed as
 * possibly undefined under `noUncheckedIndexedAccess`, and a template literal
 * would happily paste the word "undefined" into the DOM.
 */
export function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

// ── Navigation ──────────────────────────────────────────────────────────────

interface AdminNavItem {
  to: string
  icon: string
  label: string
  hint: string
  /**
   * Named when the page carries a control reserved to the server owner. The
   * mark is shown to owners only — telling an admin about a button they will
   * never see is noise, and the control itself is absent from their page.
   */
  ownerAction?: string
}

const NAV: readonly AdminNavItem[] = [
  {
    to: '/admin',
    icon: '◈',
    label: 'Server',
    hint: 'Identity, access, permissions and message policy',
    ownerAction: 'Transfer ownership',
  },
  { to: '/admin/users', icon: '♙', label: 'Users', hint: 'Accounts, roles and access' },
  { to: '/admin/invitations', icon: '✉', label: 'Invitations', hint: 'Who has been asked in' },
  { to: '/admin/notifications', icon: '◉', label: 'Notifications', hint: 'Push and email delivery' },
  { to: '/admin/webhooks', icon: '↗', label: 'Webhooks', hint: 'External systems posting into channels' },
  { to: '/admin/widgets', icon: '▦', label: 'Widgets', hint: 'Web pages embedded in channels' },
  { to: '/admin/api-keys', icon: '⚿', label: 'API keys', hint: 'Bot access for integrations' },
  {
    to: '/admin/federation',
    icon: '⇄',
    label: 'Federation',
    hint: 'Connected servers and pairing',
    ownerAction: 'Rotate the identity key',
  },
  { to: '/admin/storage', icon: '▣', label: 'Storage', hint: 'Files, capacity and uploads' },
  { to: '/admin/audit', icon: '☷', label: 'Audit', hint: 'What has been done here' },
  { to: '/admin/status', icon: '◌', label: 'Status', hint: 'Health of this server' },
]

// ── Unsaved work ────────────────────────────────────────────────────────────

interface UnsavedRegistry {
  register: (dirty: boolean) => void
}

const UnsavedContext = createContext<UnsavedRegistry | null>(null)

/**
 * Declares that this page is holding edits that have not been saved. The
 * layout then interrupts navigation away from it, and the browser interrupts a
 * reload. Registration is cleared when the page unmounts, so a page that has
 * already navigated cannot leave a guard behind.
 */
export function useUnsavedChanges(dirty: boolean): void {
  const registry = useContext(UnsavedContext)

  useEffect(() => {
    if (registry === null) return undefined

    registry.register(dirty)
    return () => {
      registry.register(false)
    }
  }, [registry, dirty])
}

// ── Reading fields the typed shapes do not enumerate ─────────────────────────

export function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/** One field of an object of unknown shape. Absent and wrong-typed read alike. */
export function readField(source: unknown, key: string): unknown {
  return asRecord(source)[key]
}

export function readText(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function readFlag(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

export function readList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function readNumbers(value: unknown): number[] {
  return readList(value).flatMap((entry) => {
    const parsed = readNumber(entry)
    return parsed === null ? [] : [parsed]
  })
}

/** A human sentence for anything thrown by the client, a network fault included. */
export function describeError(error: unknown, fallback = 'The server did not answer.'): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

/** True when a request failed because federation is switched off on this server. */
export function isFederationDisabled(error: unknown): boolean {
  return isApiError(error) && error.code === 'federation_disabled'
}

// ── Clipboard ───────────────────────────────────────────────────────────────

async function writeToClipboard(value: string): Promise<boolean> {
  try {
    if (typeof navigator.clipboard?.writeText === 'function') {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // A denied permission, or a page served over plain HTTP on a LAN address:
    // both land here, and both are recoverable by selecting the text by hand.
  }
  return false
}

/**
 * Copies a value and says so. Keys, fingerprints and invite links are the whole
 * point of several of these pages, and a copy button that fails silently is
 * worse than no copy button — so a failure asks for the value to be selected
 * instead.
 */
export function useCopy(): (value: string, what: string) => void {
  const toast = useToast()

  return useCallback(
    (value: string, what: string): void => {
      void writeToClipboard(value).then((copied) => {
        if (copied) {
          toast.success(`${what} copied to the clipboard.`)
        } else {
          toast.warning(`${what} could not be copied. Select the text and copy it by hand.`)
        }
      })
    },
    [toast],
  )
}

// ── Page chrome ─────────────────────────────────────────────────────────────

export interface AdminPageProps {
  title: string
  lead?: ReactNode
  actions?: ReactNode
  children: ReactNode
}

export function AdminPage({ title, lead, actions, children }: AdminPageProps): ReactElement {
  const navigate = useNavigate()

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div className={styles.pageHeadText}>
          <h1 className={styles.pageTitle}>{title}</h1>
          {lead !== undefined && lead !== null ? <p className={styles.pageLead}>{lead}</p> : null}
        </div>
        <div className={styles.pageActions}>
          {actions}
          <Button size="sm" variant="secondary" onClick={() => navigate('/')}>Leave settings</Button>
        </div>
      </header>
      <div className={styles.pageContent}>{children}</div>
    </div>
  )
}

export interface CardProps {
  title?: string
  lead?: ReactNode
  actions?: ReactNode
  footer?: ReactNode
  /** Marks a card whose actions cannot be undone. */
  danger?: boolean
  children: ReactNode
}

export function Card({
  title,
  lead,
  actions,
  footer,
  danger = false,
  children,
}: CardProps): ReactElement {
  const headingId = useId()
  const titled = title !== undefined && title !== ''

  return (
    <section
      className={cx(styles.card, danger && styles.cardDanger)}
      aria-labelledby={titled ? headingId : undefined}
    >
      {titled || actions !== undefined || lead !== undefined ? (
        <div className={styles.cardHead}>
          {titled ? (
            <h2 className={styles.cardTitle} id={headingId}>
              {title}
            </h2>
          ) : null}
          {actions !== undefined && actions !== null ? (
            <div className={styles.cardActions}>{actions}</div>
          ) : null}
          {lead !== undefined && lead !== null ? <p className={styles.cardLead}>{lead}</p> : null}
        </div>
      ) : null}
      {children}
      {footer !== undefined && footer !== null ? (
        <div className={styles.cardFoot}>{footer}</div>
      ) : null}
    </section>
  )
}

export function Facts({ children }: { children: ReactNode }): ReactElement {
  return <dl className={styles.facts}>{children}</dl>
}

export function Fact({ term, children }: { term: string; children: ReactNode }): ReactElement {
  return (
    <>
      <dt className={styles.factTerm}>{term}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </>
  )
}

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger'

const NOTICE_CLASS: Record<NoticeTone, string | undefined> = {
  info: styles.noticeInfo,
  success: styles.noticeSuccess,
  warning: styles.noticeWarning,
  danger: styles.noticeDanger,
}

export interface NoticeProps {
  tone?: NoticeTone
  title?: string
  children: ReactNode
}

export function Notice({ tone = 'info', title, children }: NoticeProps): ReactElement {
  return (
    <div
      className={cx(styles.notice, NOTICE_CLASS[tone])}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      {title !== undefined && title !== '' ? (
        <p className={styles.noticeTitle}>{title}</p>
      ) : null}
      <div>{children}</div>
    </div>
  )
}

/** What went wrong, in place of the thing that could not be loaded. */
export function ErrorNotice({
  error,
  title = 'This could not be loaded',
  children,
}: {
  error: unknown
  title?: string
  children?: ReactNode
}): ReactElement {
  return (
    <Notice tone="danger" title={title}>
      <p>{describeError(error)}</p>
      {children}
    </Notice>
  )
}

// ── The layout itself ───────────────────────────────────────────────────────

export function AdminLayout(): ReactElement {
  const { user, loading } = useRequireAuth()

  if (loading || user === null) {
    return <FullPageSpinner label="Opening administration" />
  }

  if (!user.is_admin) {
    return (
      <FullPageMessage
        title="Administration is not open to you"
        description="These pages are for administrators of this server. If you think that is a mistake, ask whoever runs it."
        actions={<Link to="/">Back to your rooms</Link>}
      />
    )
  }

  return <AdminChrome isOwner={user.is_owner} />
}

function AdminChrome({ isOwner }: { isOwner: boolean }): ReactElement {
  const navigate = useNavigate()
  const [dirty, setDirty] = useState(false)
  const [pending, setPending] = useState<string | null>(null)

  const registry = useMemo<UnsavedRegistry>(() => ({ register: setDirty }), [])

  // The stored answer only; the server makes no outbound request to give it.
  const updateAvailable = useAdminUpdateCheck().data?.status === 'update_available'

  // The browser's own guard covers reloads and closing the tab, which React
  // Router never sees.
  useEffect(() => {
    if (!dirty) return undefined

    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warn)
    return () => {
      window.removeEventListener('beforeunload', warn)
    }
  }, [dirty])

  const guard = (event: MouseEvent<HTMLAnchorElement>, to: string): void => {
    if (!dirty) return
    event.preventDefault()
    setPending(to)
  }

  const discard = (): void => {
    const target = pending
    setPending(null)
    setDirty(false)
    if (target !== null) navigate(target)
  }

  return (
    <UnsavedContext.Provider value={registry}>
      <div className={layout.layout}>
        <div className={layout.body}>
          <nav className={layout.nav} aria-label="Administration">
            <div className={layout.navTitle}>Administration</div>
            <ul className={layout.navList}>
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/admin'}
                    onClick={(event) => guard(event, item.to)}
                    className={({ isActive }) =>
                      cx(layout.navLink, isActive && layout.navLinkActive)
                    }
                  >
                    <span className={layout.navLabel}>
                      <span className={layout.navIcon} aria-hidden="true">{item.icon}</span>
                      {item.label}
                      {isOwner && item.ownerAction !== undefined ? (
                        <Badge tone="info">Owner</Badge>
                      ) : null}
                      {item.to === '/admin/status' && updateAvailable ? (
                        <Badge tone="warning">Update</Badge>
                      ) : null}
                    </span>
                    <span className={layout.navHint}>
                      {isOwner && item.ownerAction !== undefined
                        ? `${item.hint} · ${item.ownerAction}`
                        : item.hint}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
            {isOwner ? (
              <p className={layout.navLegend}>
                Pages marked <strong>Owner</strong> hold a control only you can use. Other
                administrators do not see it.
              </p>
            ) : null}
          </nav>

          <main className={layout.main}>
            <Routes>
              <Route index element={<SettingsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="invitations" element={<InvitationsPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
              <Route path="widgets" element={<WidgetsPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="federation" element={<FederationPage />} />
              <Route path="storage" element={<StoragePage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="status" element={<StatusPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </main>
        </div>

        <Modal
          open={pending !== null}
          onClose={() => setPending(null)}
          title="Leave without saving?"
          description="This page has changes that have not been saved. Leaving now discards them."
          size="sm"
          closeOnOverlayClick={false}
          footer={
            <>
              <Button onClick={() => setPending(null)}>Keep editing</Button>
              <Button variant="danger" onClick={discard}>
                Discard changes
              </Button>
            </>
          }
        />
      </div>
    </UnsavedContext.Provider>
  )
}
